import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { InputBaseContainer, InputSelectWithLabel, CheckboxWithLabel } from '../../../../../molecules';
import { labels } from '../constants';
import { RootState } from '../../../../../redux/root';
import { getUsersCustom } from '../../../../../api/users';
import { USER_TYPES_VALUES } from '../../../../../constants/forms';

const toBool = (v: any) => v === true || v === 'True' || v === 'true' || v === 1;

const AVOperator = ({ control, errors, setValue }) => {
  const { selectedInspection, disabledGlobalSearch } = useSelector((s: RootState) => s.inspections);
  const [avOperatorOptions, setAVOperatorOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clientOnSiteChecked, setClientOnSiteChecked] = useState(false);

  // prevents DB-hydrate effect from overwriting user changes repeatedly
  const hydratedRef = useRef(false);

  const isAvDisabled = disabledGlobalSearch || isLoading || clientOnSiteChecked;

  const selectStyles = useMemo(
    () => ({
      menu: (base: any) => ({ ...base, overflow: 'hidden' }),
      menuList: (base: any) => ({ ...base, maxHeight: 240, overflowY: 'auto' }),
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    }),
    [],
  );

  // ✅ hydrate once per inspection id
  useEffect(() => {
    if (!selectedInspection?.id) return;
    hydratedRef.current = false;
  }, [selectedInspection?.id]);

  useEffect(() => {
    if (!selectedInspection?.id) return;
    if (hydratedRef.current) return;

    const dbClientOnSite =
      selectedInspection?.is_clientonsite ??
      selectedInspection?.isClientonsite ??
      selectedInspection?.isClientOnSite ??
      selectedInspection?.clientOnSite ??
      false;

    const flag = toBool(dbClientOnSite);

    setClientOnSiteChecked(flag);
    setValue('clientonsite', flag, { shouldDirty: false, shouldValidate: false });

    if (flag) {
      setValue('avoperator', null, { shouldDirty: false, shouldValidate: false });
    }

    hydratedRef.current = true;
  }, [selectedInspection, setValue]);

  useEffect(() => {
    const fetchAVOperators = async () => {
      try {
        setIsLoading(true);
        const [inspectorsResponse, employeesResponse] = await Promise.all([
          getUsersCustom({ userRole: USER_TYPES_VALUES.INSPECTOR }),
          getUsersCustom({ userRole: USER_TYPES_VALUES.EMPLOYEE }),
        ]);

        const inspectors = inspectorsResponse.data || [];
        const employees = employeesResponse.data || [];

        const byId = new Map<number, any>();
        [...inspectors, ...employees].forEach((u: any) => {
          const idNum = Number(u.id);
          if (!Number.isNaN(idNum) && !byId.has(idNum)) byId.set(idNum, u);
        });

        const options = Array.from(byId.values()).map((user: any) => {
          const firstName = user.firstName || user.first_name || '';
          const lastName = user.lastName || user.last_name || '';
          return { id: Number(user.id), firstName, lastName, fullName: `${firstName} ${lastName}`.trim() };
        });

        setAVOperatorOptions(options);
      } catch (error) {
        console.error('Error fetching AV operators:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAVOperators();
  }, []);

  // ✅ preselect AV from DB only when NOT client on site
  useEffect(() => {
    if (clientOnSiteChecked) return;

    const inspectionAvId = Number(selectedInspection?.avoperator);
    if (!inspectionAvId || Number.isNaN(inspectionAvId)) return;
    if (!avOperatorOptions.length) return;

    const selected = avOperatorOptions.find(op => Number(op.id) === inspectionAvId);

    if (selected) {
      setValue('avoperator', selected, { shouldDirty: false, shouldValidate: false });
      return;
    }

    const fn = selectedInspection?.avoperator_first_name ?? selectedInspection?.avoperatorFirstName ?? '';
    const ln = selectedInspection?.avoperator_last_name ?? selectedInspection?.avoperatorLastName ?? '';

    if (fn || ln) {
      setValue(
        'avoperator',
        { id: inspectionAvId, firstName: fn, lastName: ln, fullName: `${fn} ${ln}`.trim() },
        { shouldDirty: false, shouldValidate: false },
      );
    }
  }, [clientOnSiteChecked, selectedInspection?.avoperator, avOperatorOptions, setValue]);

  return (
    <InputBaseContainer>
      <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
        <div style={{ width: 120, paddingRight: 12 }}>
          <span>{labels.avoperator}</span>
        </div>

        <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{ flex: '0 0 55%', minWidth: 260 }}>
            <Controller
              name="avoperator"
              control={control}
              defaultValue={null}
              as={
                <InputSelectWithLabel
                  showInRow={false}
                  placeholder="Select A/V Operator"
                  menuPlacement="bottom"
                  isDisabled={isAvDisabled}
                  error={errors.avoperator}
                  options={avOperatorOptions}
                  isClearable
                  isLoading={isLoading}
                  getOptionValue={(item: any) => String(item.id)}
                  getOptionLabel={(item: any) => item.fullName}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  menuPosition="fixed"
                />
              }
              onChange={([selected]: any) => selected}
            />
          </div>

          <div style={{ flex: 1, whiteSpace: 'nowrap' }}>
            <Controller
              name="clientonsite"
              control={control}
              valueName="checked"
              defaultValue={false}
              render={({ onChange, onBlur, value }) => (
                <CheckboxWithLabel
                  label={labels.clientOnSite || 'Client on Site'}
                  checked={!!value}
                  onBlur={onBlur}
                  onChange={(e, flag) => {
        setClientOnSiteChecked(!!flag);
        onChange(flag);
        if (flag) setValue('avoperator', null);
      }}
                  disabled={disabledGlobalSearch || isLoading}
                />
  )}
            />
          </div>
        </div>
      </div>
    </InputBaseContainer>
  );
};

export default AVOperator;
